import { RoleEditPermissionField } from "@/pages/settings/roles/edit/components/RoleEditPermissionField.tsx";
import {
  BulkPermissionType,
  getBulkPermissionChanges,
  getBulkPermissionOptions,
  getDependentPermissionChanges,
} from "@/pages/settings/roles/edit/components/role-edit-permissions.helper.ts";
import {
  PermissionOptionsInterface,
  PermissionFieldPath,
  PermissionsItem,
  PermissionsSection,
  PermissionsSectionGroup,
} from "@/pages/settings/roles/edit/role-edit.types.ts";
import {
  EditRoleSchema,
  EditRoleSchemaInput,
} from "@/pages/settings/roles/edit/use-role-edit-schema.hook.ts";
import { ApiRole, PermissionType } from "@/types/role.types.ts";
import {
  FontAwesomeIcon,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  Radio,
  Stack,
  Switch,
  Text,
  Title,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@skailer-com/uikit";
import { Fragment, useState } from "react";
import { Controller, Path, UseFormReturn, get, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

export interface RoleEditEmployeePermissionsProps {
  role?: ApiRole | undefined;
  formHook: UseFormReturn<EditRoleSchemaInput, unknown, EditRoleSchema>;
  getPermissionSections: () => PermissionsSection[];
  dataAccessActive?: boolean;
  withBulkActions?: boolean;
}

function DataAccessLabel() {
  const { t } = useTranslation();

  return (
    <Tooltip delay={false}>
      <TooltipTrigger>
        <span className={"inline-flex items-center gap-1"}>
          <FontAwesomeIcon icon={"fa-light fa-eye"} />
          <Text className={"text-md font-normal"}>{t("core:data_access_rules_applied")}</Text>
        </span>
      </TooltipTrigger>
      <TooltipContent className={"max-w-60"}>
        {t("core:data_access_rules_applied_tooltip")}
      </TooltipContent>
    </Tooltip>
  );
}

function PermissionItemRenderer({
  item,
  index,
  control,
  register,
  indented,
  dataAccessActive,
  onPermissionChange,
}: {
  item: PermissionsItem;
  index: number;
  control: UseFormReturn<EditRoleSchemaInput, unknown, EditRoleSchema>["control"];
  register: UseFormReturn<EditRoleSchemaInput, unknown, EditRoleSchema>["register"];
  indented?: boolean;
  dataAccessActive?: boolean;
  onPermissionChange: (field: PermissionsItem["field"], value: PermissionType) => void;
}) {
  const parentValue = useWatch({
    control,
    name: item.field as Path<EditRoleSchemaInput>,
  });
  const dependencyFields = item.dependency?.fields ?? [];
  const dependencyValues = useWatch({
    control,
    name: dependencyFields as Path<EditRoleSchemaInput>[],
  });
  const getPermission = (field: PermissionFieldPath) => {
    const fieldIndex = dependencyFields.indexOf(field);
    return fieldIndex === -1 ? undefined : (dependencyValues[fieldIndex] as PermissionType);
  };
  const dependencyState = item.dependency?.getState(getPermission);
  const disabled = dependencyState?.disabled ?? false;

  const wrapper = indented ? "pl-(--gap-lg)" : "";

  return (
    <>
      {item.renderAs == PermissionOptionsInterface.switch ? (
        <Controller
          key={`employee-switch-permission-${index}`}
          render={({ field: { value, onChange } }) => (
            <div className={wrapper}>
              <Switch
                checked={!disabled && value == PermissionType.edit}
                disabled={disabled}
                onChange={(newValue) => {
                  const permission = newValue ? PermissionType.edit : PermissionType.none;
                  onChange(permission);
                  onPermissionChange(item.field, permission);
                }}
                size={"sm"}
                title={item.title}
                titleAfterSlot={
                  item.dataAccessDependent && dataAccessActive ? <DataAccessLabel /> : undefined
                }
                description={item.description}
              />
            </div>
          )}
          control={control}
          name={item.field as Path<EditRoleSchemaInput>}
        />
      ) : (
        <div className={wrapper}>
          <RoleEditPermissionField
            item={item}
            register={register}
            control={control}
            key={`employee-field-permission-${index}`}
          />
        </div>
      )}
      {item.children &&
        parentValue == PermissionType.edit &&
        item.children.map((child, childIndex) => (
          <PermissionItemRenderer
            key={`child-${index}-${childIndex}`}
            item={child}
            index={childIndex}
            control={control}
            register={register}
            indented={true}
            dataAccessActive={dataAccessActive}
            onPermissionChange={onPermissionChange}
          />
        ))}
    </>
  );
}

function ConditionalGroup({
  group,
  control,
  register,
  dataAccessActive,
  onPermissionChange,
}: {
  group: PermissionsSectionGroup;
  control: UseFormReturn<EditRoleSchemaInput, unknown, EditRoleSchema>["control"];
  register: UseFormReturn<EditRoleSchemaInput, unknown, EditRoleSchema>["register"];
  dataAccessActive?: boolean;
  onPermissionChange: (field: PermissionsItem["field"], value: PermissionType) => void;
}) {
  const watchedValue = useWatch({
    control,
    name: (group.hiddenWhenField ?? "") as Path<EditRoleSchemaInput>,
  });

  if (group.hiddenWhenField && watchedValue == group.hiddenWhenValue) {
    return null;
  }

  return (
    <>
      {group.title && (
        <Title
          header={group.title}
          caption={group.description}
          size={"xs"}
          paddingBottom
          paddingTop
          className={"text-secondary"}
        />
      )}
      {group.items.map((item, index) => (
        <PermissionItemRenderer
          key={`permission-item-${index}`}
          item={item}
          index={index}
          control={control}
          register={register}
          dataAccessActive={dataAccessActive}
          onPermissionChange={onPermissionChange}
        />
      ))}
    </>
  );
}

export const RoleEditPermissions = ({
  formHook,
  getPermissionSections,
  dataAccessActive,
  withBulkActions = false,
}: RoleEditEmployeePermissionsProps) => {
  const { register, control, getValues, setValue } = formHook;
  const { t } = useTranslation();

  const [selectedSectionIndex, setSelectedSectionIndex] = useState(0);
  const sections = getPermissionSections();
  const handlePermissionChange = (field: PermissionsItem["field"], value: PermissionType) => {
    getDependentPermissionChanges(sections, field, value, (permissionField) =>
      get(getValues(), permissionField)
    ).forEach((dependentValue, dependentField) => {
      setValue(dependentField as Path<EditRoleSchemaInput>, dependentValue as never, {
        shouldDirty: true,
        shouldValidate: true,
      });
    });
  };
  const applyToSection = (section: PermissionsSection, permission: BulkPermissionType) => {
    getBulkPermissionChanges(section, permission).forEach((value, field) => {
      setValue(field as Path<EditRoleSchemaInput>, value as never, {
        shouldDirty: true,
        shouldValidate: true,
      });
    });
  };

  const getBulkPermissionTitle = (permission: BulkPermissionType) => {
    const translationKeys = {
      [PermissionType.none]: "core:set_all_permissions_none",
      [PermissionType.view]: "core:set_all_permissions_view",
      [PermissionType.edit]: "core:set_all_permissions_edit",
    } as const;

    return t(translationKeys[permission]);
  };

  return (
    <Stack>
      <div className={"grid flex-1 md:grid-cols-[300px_auto]"}>
        <Stack className={"pt-(--gap-md)"}>
          {sections.map((section, sectionIndex) => (
            <Fragment key={section.title}>
              {section.sectionHeader && (
                <Title header={section.sectionHeader} size={"xs"} paddingBottom paddingTop />
              )}
              <div>
                <Radio
                  onClick={() => {
                    setSelectedSectionIndex(sectionIndex);
                  }}
                  checked={selectedSectionIndex == sectionIndex}
                  title={section.title}
                />
              </div>
            </Fragment>
          ))}
        </Stack>
        <Stack>
          {sections.map((section, sectionIndex) => (
            <Stack
              key={section.title}
              gap={"sm"}
              className={selectedSectionIndex == sectionIndex ? "" : "hidden"}
            >
              {withBulkActions && (
                <Stack
                  direction={"horizontal"}
                  items={"center"}
                  justify={"between"}
                  gap={"md"}
                  className={"bg-dark/5 rounded-xs px-4 py-3 mt-(--gap-md) flex-wrap"}
                >
                  <Text className={"font-medium"}>{t("core:set_all_permissions")}</Text>
                  <DropdownMenu
                    trigger={
                      <Button variant={"secondary"}>{t("core:set_all_permissions_select")}</Button>
                    }
                  >
                    {getBulkPermissionOptions(section).map((permission) => (
                      <DropdownMenuItem
                        key={permission}
                        title={getBulkPermissionTitle(permission)}
                        onClick={() => applyToSection(section, permission)}
                      />
                    ))}
                  </DropdownMenu>
                </Stack>
              )}
              {section.groups.map((group, groupIndex) => (
                <Fragment key={`${group.title}-${groupIndex}`}>
                  {group.hiddenWhenField ? (
                    <ConditionalGroup
                      group={group}
                      control={control}
                      register={register}
                      dataAccessActive={dataAccessActive}
                      onPermissionChange={handlePermissionChange}
                    />
                  ) : (
                    <>
                      {group.title && (
                        <Title
                          header={group.title}
                          caption={group.description}
                          size={"xs"}
                          paddingBottom
                          paddingTop
                          className={"text-secondary"}
                        />
                      )}
                      {group.items.map((item, index) => (
                        <PermissionItemRenderer
                          key={`permission-item-${index}`}
                          item={item}
                          index={index}
                          control={control}
                          register={register}
                          dataAccessActive={dataAccessActive}
                          onPermissionChange={handlePermissionChange}
                        />
                      ))}
                    </>
                  )}
                </Fragment>
              ))}
            </Stack>
          ))}
        </Stack>
      </div>
    </Stack>
  );
};
